import React from 'react';
import { motion } from 'framer-motion';
import { Users, Trophy, CalendarCheck } from 'lucide-react';

interface AnalyticsTopClientsProps {
    data: { id: string; name: string; count: number }[];
}

const AnalyticsTopClients: React.FC<AnalyticsTopClientsProps> = ({ data }) => {

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    const topClient = data.length > 0 ? data[0] : null;
    const otherClients = data.length > 1 ? data.slice(1, 5) : []; // Mostriamo fino a 5 clienti in totale

    return (
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 flex flex-col h-full">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Clienti Top</h3>
                    <p className="text-sm text-gray-400 mt-1">Chi prenota più spesso</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                    <Users size={20} className="text-gray-400" />
                </div>
            </div>

            <div className="flex-1 flex flex-col gap-3">
                {data.length > 0 ? (
                    <>
                        {/* 1. TOP CLIENT (GOLD CARD) */}
                        {topClient && (
                            <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-amber-100 flex items-center justify-between shadow-sm relative overflow-hidden">
                                <div className="flex items-center gap-3 relative z-10">
                                    <div className="w-10 h-10 bg-white text-amber-500 rounded-full flex items-center justify-center shadow-sm border border-amber-100">
                                        <Trophy size={18} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-gray-900 text-sm">{topClient.name}</p>
                                            <span className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">#1</span>
                                        </div>
                                        <p className="text-xs text-amber-600/80 font-medium">Cliente più fedele</p>
                                    </div>
                                </div>
                                <div className="text-right relative z-10">
                                    <p className="text-lg font-bold text-gray-900">{topClient.count}</p>
                                    <p className="text-[10px] text-gray-500 uppercase font-medium">Appuntamenti</p>
                                </div>
                            </div>
                        )}

                        {/* 2. ALTRI CLIENTI */}
                        {otherClients.map((client, idx) => (
                            <div key={client.id} className="p-4 bg-white rounded-xl border border-gray-100 flex items-center justify-between hover:border-gray-300 hover:shadow-sm transition-all">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center font-bold text-sm">
                                        {idx + 2}
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-700 text-sm">{client.name}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-gray-700">{client.count}</p>
                                    <p className="text-[10px] text-gray-400 uppercase">Appuntamenti</p>
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm">
                            <CalendarCheck size={20} className="text-gray-300" />
                        </div>
                        <h4 className="text-sm font-bold text-gray-900">Nessun dato</h4>
                        <p className="text-xs text-gray-400 mt-1 max-w-[200px]">Non ci sono abbastanza prenotazioni nel periodo selezionato.</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default AnalyticsTopClients;